/**
 * ChatGPT Ads Measurement Pixel (oaiq) for www.caire.se.
 *
 * Before consent the page makes no request to OpenAI. The site's single
 * consent cookie (`CONSENT_COOKIE`) decides:
 * - a returning visitor with "all" gets the SDK from the SSR head (server.ts,
 *   `openAiPixelHeadSnippet`), so an ad landing keeps its attribution id
 *   before React mounts;
 * - a visitor who accepts in the banner gets the SDK at that moment, on the
 *   same landing URL, through `setOpenAiPixelConsent(true)`, then a page view;
 * - "Avvisa alla", or no choice, never loads it.
 * Reference: https://developers.openai.com/ads/measurement-pixel
 */

/** Pixel "My first pixel" in the Caire OpenAI Ads account. */
export const OPENAI_ADS_PIXEL_ID = "X2EoPQcuhb9zmoVmQifsCS";

import { CONSENT_COOKIE } from "./cookie-utils";

export const OAIQ_SDK_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";

type OaiqArg = string | boolean | Record<string, unknown>;

declare global {
  interface Window {
    oaiq?: (...args: OaiqArg[]) => void;
  }
}

/** True when a `document.cookie` or request `Cookie` header holds "all". */
export function hasAllConsent(cookies: string | null | undefined): boolean {
  if (!cookies) return false;
  return cookies
    .split(";")
    .some((part) => part.trim() === `${CONSENT_COOKIE}=all`);
}

/**
 * Inline head script for a returning visitor whose request cookie already says
 * "all"; an empty string otherwise, so before consent the page makes no request
 * to OpenAI at all. The browser re-checks the cookie before loading.
 */
export function openAiPixelHeadSnippet(
  pixelId: string,
  requestCookieHeader: string | null | undefined,
): string {
  if (!hasAllConsent(requestCookieHeader)) return "";
  return `<!-- ChatGPT Ads Measurement Pixel -->
<script>
  if (/(?:^|;\\s*)${CONSENT_COOKIE}=all(?:;|$)/.test(document.cookie)) {
  !function(w,d,s,u){if(w.oaiq)return;var q=function(){q.q.push(arguments)};q.q=[];w.oaiq=q;var j=d.createElement(s);j.async=1;j.src=u;var f=d.getElementsByTagName(s)[0];f.parentNode.insertBefore(j,f)}(window,document,"script","${OAIQ_SDK_URL}");
  oaiq("init", { pixelId: ${JSON.stringify(pixelId)} });
  }
</script>
`;
}

/**
 * The same queue and script insertion as the head loader, for a visitor who
 * accepts during this visit. It runs on the landing URL itself, so the SDK
 * still sees the ChatGPT ad attribution parameters.
 */
function loadOaiqSdk(): void {
  const calls: IArguments[] = [];
  const queue = function () {
    // eslint-disable-next-line prefer-rest-params -- the SDK replays `arguments` objects
    calls.push(arguments);
  } as unknown as NonNullable<Window["oaiq"]> & { q: IArguments[] };
  queue.q = calls;
  window.oaiq = queue;
  const script = document.createElement("script");
  script.async = true;
  script.src = OAIQ_SDK_URL;
  document.head.appendChild(script);
  oaiq("init", { pixelId: OPENAI_ADS_PIXEL_ID });
}

function oaiq(...args: OaiqArg[]): void {
  if (typeof window === "undefined" || typeof window.oaiq !== "function")
    return;
  try {
    window.oaiq(...args);
  } catch (error) {
    console.error("Error sending OpenAI pixel command:", error);
  }
}

/**
 * Granting loads the SDK if the head did not (an accept made during this
 * visit). Denying never loads it, and tells an already loaded SDK to stop.
 */
export function setOpenAiPixelConsent(granted: boolean): void {
  if (typeof window === "undefined") return;
  if (granted && typeof window.oaiq !== "function") loadOaiqSdk();
  oaiq("consent", granted);
}

let lastPageViewPath: string | undefined;

export function measureOpenAiPageView(path: string, title: string): void {
  if (typeof window === "undefined" || typeof window.oaiq !== "function")
    return;
  // Route tracking and the accept handler can both report the landing page.
  if (lastPageViewPath === path) return;
  lastPageViewPath = path;
  oaiq("measure", "page_viewed", {
    type: "contents",
    contents: [{ id: path, name: title, content_type: "page" }],
  });
}

/** Free-analysis start: the campaign's primary conversion. */
export function measureOpenAiLead(): void {
  oaiq("measure", "lead_created", { type: "customer_action" });
}

/** A demo booked through the Cal embed. */
export function measureOpenAiAppointment(): void {
  oaiq("measure", "appointment_scheduled", { type: "customer_action" });
}
